import React from 'react'
import { Provider as ReactReduxProvider } from 'react-redux'
import { setGlobalReduxStore } from '@web/redux/global_redux_store'
import {
  type Action,
  type AnyAction,
  applyMiddleware,
  compose,
  createStore,
  type DeepPartial,
  type Middleware,
  type Reducer,
  type Store,
} from 'redux'
import thunk from 'redux-thunk'

interface ReduxProviderProps<S, A extends Action = AnyAction> {
  reducer: Reducer<S, A>
  /**
   * Additional middleware, `thunk` always included.
   */
  middleware?: Middleware<{}, S>[]
  preloadedState?: DeepPartial<S>
  /** Called once after createStore. */
  onCreate?: (store: Store<S, A>) => void
  children: React.ReactNode
}

function getReduxDevtoolsEnhancer() {
  if (!FIGBUILD_PROD && typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__) {
    return window.__REDUX_DEVTOOLS_EXTENSION__()
  }
  return (next: any) => next
}

export function ReduxProvider<S, A extends Action = AnyAction>({
  reducer,
  middleware = [],
  preloadedState,
  onCreate,
  children,
}: ReduxProviderProps<S, A>) {
  const [store] = React.useState<Store<S, A>>(() => {
    const enhancer = compose(applyMiddleware(thunk, ...middleware), getReduxDevtoolsEnhancer())
    const store = (
      preloadedState !== undefined
        ? createStore(reducer, preloadedState, enhancer)
        : createStore(reducer, enhancer)
    ) as Store<S, A>
    setGlobalReduxStore(store)
    onCreate?.(store)
    return store
  })
  return <ReactReduxProvider store={store}>{children}</ReactReduxProvider>
}
